import { SortDescriptor, State, process, DataResult, GroupDescriptor, groupBy, GroupResult } from '@progress/kendo-data-query';
import {
  Grid,
  GridCustomRowProps,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridGroupExpandChangeEvent,
  GridNoRecords,
  GridRowClickEvent,
} from '@progress/kendo-react-grid';
import { Loader } from '@progress/kendo-react-indicators';
import React, { useEffect, useState } from 'react';
import './Table.scss';
import { ConvertRowData } from './TableHelper';
import CustomButton from '../../stateless/customButton/CustomButton';
import { setExpandedState, setGroupIds } from '@progress/kendo-react-data-tools';
import { MyCommandCell } from "./MyCommandCell";
import { IconsContext, IconsContextType } from '@progress/kendo-react-common';
import { plusIcon, minusIcon } from '@progress/kendo-svg-icons';

interface ITableProps {
  values: any;
  columns: any;
  tableName: string;
  height: string;
  customColumns?: any[];
  details?: (detailProps: GridDetailRowProps) => any;
  onClickAdd?: () => void;
  onDetailExpandChange?: (data: any) => void;
  group?: GroupDescriptor[];
  notPageable?: boolean;
  notFilterable?: boolean;
  notSortable?: boolean;
  onRowClick?: (event: GridRowClickEvent) => void;
  editField?: string | undefined;
  isLoading?: boolean;
  onButtonClick?: (row: any) => void;
  buttonText?: string;
  wrapHeaders?: boolean;
  isDocket?: boolean;
  onClickAddText?: string;
  dataItemKey?: string;
}

/**
 *
 * @param props
 * @param values populates the tables rows data
 * @param columns array of row definitions (field, title, filter(numeric, text))
 * @param tableName label shown at the top of the table
 * @param height  set the table height
 * @param details (detailProps: GridDetailRowProps) => TSX component
 * @example
 * // See the TableHelper for generating column definitions
 * // Any new custom column generator can be added to the table helper file
 * <Table
 * values = {
 *  rows: [{field: "test"}, {field: "test2"}],
 *  columns: [{field: "test", name: "Test column"}, {field: "test2", name: "Second test column"}]
 * }
 * tableName = "Dashboard"
 * height = "90vh"
 * />
 * @example
 * //The detail props expects a function that returns a .TSX component
 * //And needs to accept in (detailProps: GridDetailRowProps)
 * const DetailComponent = (detailProps: GridDetailRowProps) => {
 * const dataItem = detailProps.dataItem;
 *  return (
 *    <section>
 *      <p>Filing Name: {dataItem.filingName}</p>
 *      <p>Title Id: {dataItem.titleId}</p>
 *    </section>
 *  );
 * };
 * @returns
 */

function Table(props: ITableProps) {
  const [values, setValues] = useState<any[]>(ConvertRowData(props.values, props.isDocket));
  const [columns, setColumns] = useState<any[]>(props.columns);
  const initialSort: Array<SortDescriptor> = [
    { field: "docket", dir: "desc" },
  ];
  const [sort] = React.useState(initialSort);

  // State to track which detail rows are expanded
  const [detailExpandState, setDetailExpandState] = useState<{ [key: string]: boolean }>({});


  // const initialFilter: CompositeFilterDescriptor = {
  //   logic: "and",
  //   filters: [{ field: "filingName", operator: "contains", value: "" }],
  // };
  // const [filter, setFilter] = React.useState(initialFilter);
  // const [data, setData] = React.useState<any>();
  const [dataState, setDataState] = useState<State>(props.notPageable ? {} : {
    take: 20,
    skip: 0,
    // sort: [{ field: "id", dir: "desc" }],
  });

  useEffect(() => {
    setValues(ConvertRowData(props.values, props.isDocket))
    setDataResult(process(ConvertRowData(props.values, props.isDocket), dataState));
    setGroupResult(processWithGroups(ConvertRowData(props.values, props.isDocket), props.group ?? []))
    setColumns(props.columns)
    // Only reset detail expand state when values actually change, not on every render
    if (JSON.stringify(values) !== JSON.stringify(ConvertRowData(props.values, props.isDocket))) {
      setDetailExpandState({});
    }

    setTimeout(() => {
      document.querySelectorAll('.k-reset a').forEach(element => element.setAttribute('role', 'button'))
    }, 100)
    return () => {
      setValues([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValues, dataState, props.values, props.columns])

  const [dataResult, setDataResult] = useState<DataResult>(
    process(props.values, dataState)
  );

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataResult(process(values, event.dataState));
    setDataState(event.dataState);
  };

  const expandChange = (event: any) => {
    // Update the detail expand state
    setDetailExpandState(event.detailExpand || {});

    // If there's a callback for expand changes, call it
    if (props.onDetailExpandChange) {
      const detailExpandKeys = Object.keys(event.detailExpand || {});
      if (detailExpandKeys.length > 0) {
        const toggledId = detailExpandKeys[0];
        const dataItemKey = props.dataItemKey || 'id';
        const dataItem = values.find((item: any) => String(item[dataItemKey]) === String(toggledId));

        if (dataItem) {
          props.onDetailExpandChange(dataItem);
        }
      }
    }
  };

  //#region grouped table

  const processWithGroups = (data: any[], group: GroupDescriptor[]) => {
    const newDataState = groupBy(data, group);
    setGroupIds({ data: newDataState, group: group });
    return newDataState;
  }

  const [groupResult, setGroupResult] = useState<GroupResult[]>(
    processWithGroups(props.values, props.group ?? [])
  );

  const [collapsedState, setCollapsedState] = useState<string[]>([]);

  const groupExpandChange = (event: GridGroupExpandChangeEvent) => {
    if ((event as any).data && (event as any).data.groupId) {
      const groupId = (event as any).data.groupId;
      const isExpanded = !(collapsedState.includes(groupId));
      const newCollapsedIds = isExpanded
        ? [...collapsedState, groupId]
        : collapsedState.filter((id) => id !== groupId);
      setCollapsedState(newCollapsedIds);
    }
  }

  const groupExpandedState = setExpandedState({
    data: groupResult,
    collapsedIds: collapsedState,
  });

  //#endregion

  const editField: string = "inEdit";

  const ButtonCell = (gridCellProps: GridCellProps) => (
    <MyCommandCell {...gridCellProps} onClick={props.onButtonClick} editField={editField} text={props.buttonText} />
  );

  const CustomRow = (props: GridCustomRowProps) => {
    const { children, trProps } = props;
    // Add pointer-cursor class if onRowClick is provided
    const className = props.onClick ? 'pointer-cursor' : trProps?.className;
    return (
      <tr {...trProps} className={className}>
        {children}
      </tr>
    );
  };

  const iconsValue: IconsContextType = {
    icons: {
      plus: plusIcon, 
      minus: minusIcon 
    }
  };

  return (
    <div className="table-wrapper">
      <div className="header">
        <p className="table-label">{props.tableName}</p>
        {props.onClickAdd ? (
          <CustomButton fillMode="solid" themeColor="primary" title="Add" onClick={props.onClickAdd} icon="add" size="medium">{props.onClickAddText ?? ""}</CustomButton>
        ) : null}
      </div>
      {(props.isLoading === undefined || !props.isLoading) && props.columns && props.values ? (
        <IconsContext.Provider
          value={iconsValue}>
        <Grid
          filterable={!props.notFilterable}
          sortable={!props.notSortable}
          pageable={props.notPageable ? false : { buttonCount: 4, pageSizes: true }}
          {...dataState}
          data={props.group ? groupExpandedState : dataResult}
          sort={sort}
          onDataStateChange={dataStateChange}
          style={{ height: props.height }}
          dataItemKey={props.dataItemKey || 'id'}
          detailExpand={detailExpandState}
          onDetailExpandChange={props.details ? expandChange : undefined}
          onGroupExpandChange={props.group ? groupExpandChange : undefined}
          detail={props.details ?? null}
          group={props.group}
          onRowClick={props.onRowClick}
          edit={{ [`${props.editField}`]: true }}
          resizable
          rows={{ data: CustomRow }}
          navigatable={true}
        >
          <GridNoRecords>
            <div className="no-records-table">
              No Records Found
            </div>
          </GridNoRecords>
          {columns ?
            columns.map((column: any) => {
              if (column.field === "button") {
                return (<Column key={column.field} cells={{ data: ButtonCell }} />);
              }

              if (column.hidden === false) {
                if (!!props.wrapHeaders) {

                  if (column.width !== 0) {
                    return (<Column key={column.field} field={column.field} title={column.title} filter={column.text} width={column.width} headerClassName='wrap-header' />);
                  }

                  return (<Column key={column.field} field={column.field} title={column.title} filter={column.text} headerClassName='wrap-header' />);
                }

                if (column.width !== 0) {
                  return (<Column key={column.field} field={column.field} title={column.title} width={column.width} filter={column.text} />);
                }

                return (<Column key={column.field} field={column.field} title={column.title} filter={column.text} />);
              }
              else {
                return null;
              }
            })
            : null
          }
        </Grid>
        </IconsContext.Provider>
      ) : (
        <div style={{ width: "90vw" }}>
          {/* if loading show spinner */}
          <Loader type="converging-spinner" />
        </div>
      )}
    </div>
  )
}

export default Table